import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Stack,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const menuItems = [
    { label: 'Ventures', href: '/ventures', isRoute: true },
    { label: 'About', href: '/about', isRoute: true },
  ];

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          bgcolor: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          top: 0, 
          zIndex: 1100,
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            <Typography
              variant="h6"
              component="div"
              onClick={() => navigate('/')}
              sx={{ 
                flexGrow: 1, 
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              Actuation
            </Typography>
            
            {/* Desktop Navigation */}
            {!isMobile && (
              <Stack direction="row" spacing={2}>
                <Button 
                  href="https://calendly.com/actuation/30min" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    px: 2.5,
                    py: 0.75,
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 252, 248, 0.95)',
                    color: '#1a1a1a',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#fff',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(255, 255, 255, 0.15)',
                    },
                  }}
                >
                  Schedule a Call
                </Button>
                <Button 
                  color="inherit"
                  onClick={() => navigate('/ventures')}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 400,
                    px: 2,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Ventures
                </Button>
                <Button 
                  color="inherit"
                  onClick={() => navigate('/about')}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 400,
                    px: 2,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  About
                </Button>
              </Stack>
            )}

            {/* Mobile Navigation */}
            {isMobile && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button 
                  href="https://calendly.com/actuation/30min" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 252, 248, 0.95)',
                    color: '#1a1a1a',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#fff',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Schedule a Call
                </Button>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                >
                  <MenuIcon />
                </IconButton>
              </Stack>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            color: 'white',
            borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <IconButton
            color="inherit"
            onClick={toggleDrawer(false)}
            sx={{ 
              color: 'white',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                transform: 'rotate(90deg)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ pt: 2 }}>
          {menuItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ px: 1, mb: 0.5 }}>
              <ListItemButton
                component="a"
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer' : undefined}
                onClick={(e) => {
                  if (item.isRoute) {
                    e.preventDefault();
                    navigate(item.href);
                  }
                  toggleDrawer(false)();
                }}
                sx={{
                  color: 'white',
                  borderRadius: 2,
                  py: 1.5,
                  px: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    transform: 'translateX(-4px)',
                  },
                }}
              >
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '1rem',
                    fontWeight: 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
}

export default Header;

